/**
 * Article Metadata Block
 *
 * Displays blog-style metadata for Living Project Guide pages:
 * - Reading time
 * - Last updated date
 * - Category badge
 * - Breadcrumb navigation
 */

import { Anchor, Badge, Box, Breadcrumbs, Group, Text } from '@mantine/core'
import { IconBook } from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import { getCategoryLabel } from '../utils'

interface ProjectGuideMetaProps {
  category: string
  readingTimeMinutes: number
  updatedAt?: number
  title: string
}

function formatDate(timestamp?: number): string {
  if (!timestamp) return 'Recently'
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function ProjectGuideMeta({
  category,
  readingTimeMinutes,
  updatedAt,
  title,
}: ProjectGuideMetaProps) {
  const categoryLabel = getCategoryLabel(category)

  return (
    <Box py="xs">
      <Group gap="md" wrap="wrap">
        <Group gap="xs">
          <IconBook size={14} color="var(--mantine-color-dimmed)" />
          <Text size="xs" c="dimmed">
            {readingTimeMinutes} min reading time
          </Text>
        </Group>
        <Text size="xs" c="dimmed">
          •
        </Text>
        <Text size="xs" c="dimmed">
          Updated {formatDate(updatedAt)}
        </Text>
        <Text size="xs" c="dimmed">
          •
        </Text>
        <Badge size="xs" variant="light">
          {categoryLabel}
        </Badge>
      </Group>

      <Breadcrumbs mt="xs" separator="›">
        <Anchor component={Link} to="/" size="xs" c="dimmed">
          Home
        </Anchor>
        <Anchor component={Link} to="/diy" size="xs" c="dimmed">
          DIY Projects
        </Anchor>
        <Text size="xs" c="dimmed">
          {categoryLabel}
        </Text>
        <Text size="xs" c="dimmed">
          {title}
        </Text>
      </Breadcrumbs>
    </Box>
  )
}
